'use client';

import clsx from 'clsx';
import { useRef, useState } from 'react';
import { Autoplay, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper/types';
import 'swiper/css';
import Icon from '@publishing/components/ui/Icon';
import { ArrowChevron } from '@publishing/components/ui/SvgSprite';

import { getEventKey, type HubEvent } from '../data';
import s from '../page.module.scss';
import { HubSlideItem } from './HubSlideItem';

interface HubSlideBannerProps {
  events: HubEvent[];
}

export function HubSlideBanner({ events }: HubSlideBannerProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const [dotsEl, setDotsEl] = useState<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);

  if (events.length === 0) return null;

  const canSlide = events.length > 1;

  return (
    <div className={clsx(s.slide_wrap, !ready && s.pending)}>
      <Swiper
        modules={[Autoplay, Pagination]}
        slidesPerView={1}
        speed={700}
        loop={canSlide}
        autoplay={canSlide ? { delay: 3500, disableOnInteraction: false } : false}
        spaceBetween={0}
        className={s.slide}
        allowTouchMove={canSlide}
        onSwiper={(swiper: SwiperType) => {
          swiperRef.current = swiper;
          setReady(true);
        }}
        pagination={canSlide && dotsEl ? { el: dotsEl, clickable: true } : false}
      >
        {events.map((event) => (
          <SwiperSlide key={getEventKey(event)}>
            <HubSlideItem event={event} />
          </SwiperSlide>
        ))}
      </Swiper>

      {canSlide && (
        <div className={s.slide_controls}>
          <button
            type="button"
            className={s.slide_prev}
            aria-label="이전 슬라이드"
            onClick={() => swiperRef.current?.slidePrev()}
          >
            <Icon name={ArrowChevron} direction="left" size={24} />
          </button>
          <button
            type="button"
            className={s.slide_next}
            aria-label="다음 슬라이드"
            onClick={() => swiperRef.current?.slideNext()}
          >
            <Icon name={ArrowChevron} direction="right" size={24} />
          </button>
        </div>
      )}

      {canSlide && <div ref={setDotsEl} className={s.slide_dots} />}
    </div>
  );
}
